import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SECTION_THEME, BaseSectionSettings } from './section-renderer.models';
import { WeekViewComponent, AggregatedSlot, BookingFormComponent, BookingRequest, PublicI18nService, BookingRequestResponse } from '@frontend/public-calendar';
import { generateMockSlots } from '../models/preview.models';
import { DEFAULT_TIMEZONE } from '@frontend/util';

/**
 * Calendar section settings
 */
export interface CalendarSettings extends BaseSectionSettings {
  layoutVariant?: 'default' | 'compact';
  showTitle?: boolean;
  showSubtitle?: boolean;
}

/**
 * Calendar section content
 */
export interface CalendarContent {
  title?: string;
  subtitle?: string;
}

/**
 * Event emitted when the week view navigates to a new week.
 */
export interface WeekChangeEvent {
  from: string;
  to: string;
}

/**
 * Calendar Section Renderer
 * Renders the booking calendar as a full-width section.
 * Uses theme injection for consistent styling with other sections.
 * Supports both preview mode (mock slots) and live mode (real API slots).
 */
@Component({
  selector: 'dcp-calendar-renderer',
  standalone: true,
  imports: [CommonModule, WeekViewComponent, BookingFormComponent],
  template: `
    <div
      [class]="sectionSpacingClass()"
      [style.background-color]="backgroundColor()"
      [style.--color-primary]="theme.primaryColor"
      [style.--color-border]="theme.borderColor"
      [style.--color-surface]="theme.surfaceColor"
      [style.--color-foreground]="theme.textColor"
      [style.--color-muted]="theme.textMutedColor"
    >
      <div [class]="'px-4 sm:px-6 lg:px-8 mx-auto ' + containerWidthClass()">
        <!-- Section Header -->
        @if (showHeader()) {
          <div class="mx-auto max-w-2xl lg:mx-0">
            @if (settings().showTitle !== false && content().title) {
              <h2
                class="text-pretty text-4xl font-semibold tracking-tight sm:text-5xl"
                [style.color]="theme.textColor"
              >
                {{ content().title }}
              </h2>
            }
            @if (settings().showSubtitle !== false && content().subtitle) {
              <p
                class="mt-6 text-lg/8"
                [style.color]="theme.textMutedColor"
              >
                {{ content().subtitle }}
              </p>
            }
          </div>
        }

        <!-- Calendar week view -->
        <div [class]="showHeader() ? 'mt-12' : ''">
          <pc-week-view
            [slots]="displaySlots()"
            [loading]="loading()"
            [selectedSlot]="selectedSlot()"
            [timezone]="timezone()"
            (slotSelected)="onSlotSelected($event)"
            (weekChanged)="onWeekChanged($event)"
          />
        </div>

        <!-- Booking form (appears when slot is selected) -->
        @if (selectedSlot() && !previewMode()) {
          <div class="mt-6 max-w-2xl mx-auto">
            <pc-booking-form
              [selectedSlot]="selectedSlot()!"
              [submitting]="submitting()"
              [errorMessage]="formErrorMessage()"
              (submitRequest)="onSubmitRequest($event)"
              (changeSlot)="clearSlotSelection()"
            />
          </div>
        }

        <!-- Preview mode: show disabled form UI -->
        @if (selectedSlot() && previewMode()) {
          <div class="mt-6 max-w-2xl mx-auto">
            <pc-booking-form
              [selectedSlot]="selectedSlot()!"
              [submitting]="false"
              [errorMessage]="null"
              (changeSlot)="clearSlotSelection()"
            />
            <!-- Preview mode notice on form -->
            <p class="mt-3 text-center text-sm" [style.color]="theme.textMutedColor">
              <span class="inline-flex items-center gap-1">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m0 0v2m0-2h2m-2 0H10m9.364-7.364l-1.414 1.414M6.05 17.95l-1.414 1.414M12 5V3m0 16v2M5.636 5.636L4.222 4.222m15.556 0L18.364 5.636" />
                </svg>
                Form submission is disabled in preview mode
              </span>
            </p>
          </div>
        }

        <!-- Preview notice (only in preview mode when no slot selected) -->
        @if (!selectedSlot() && previewMode()) {
          <p class="mt-4 text-center text-sm" [style.color]="theme.textMutedColor">
            <span class="inline-flex items-center gap-1">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Preview mode — slots shown are for demonstration only
            </span>
          </p>
        }
      </div>
    </div>

    <!-- Themed Success Modal -->
    @if (showSuccess()) {
      <div
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        [style.background-color]="'rgba(0,0,0,0.5)'"
        (click)="onDismissSuccess()"
        (keyup.escape)="onDismissSuccess()"
        role="dialog"
        aria-modal="true"
        aria-labelledby="success-modal-title"
      >
        <div
          class="relative max-w-md w-full max-h-[90vh] overflow-y-auto rounded-lg shadow-xl p-8 text-center animate-in fade-in zoom-in duration-200"
          [style.background-color]="theme.surfaceColor"
          [style.border-radius]="theme.borderRadius === 'full' ? '1rem' : theme.borderRadius === 'xl' ? '0.75rem' : theme.borderRadius === 'lg' ? '0.5rem' : theme.borderRadius === 'md' ? '0.375rem' : theme.borderRadius === 'sm' ? '0.25rem' : '0'"
          [style.border]="'1px solid ' + theme.borderColor"
          (click)="$event.stopPropagation()"
          (keyup.enter)="$event.stopPropagation()"
        >
          <!-- Success icon -->
          <div
            class="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4"
            [style.background-color]="theme.primaryColor + '20'"
          >
            <svg class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" [style.color]="theme.primaryColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h2
            id="success-modal-title"
            class="text-2xl font-bold mb-2"
            [style.color]="theme.textColor"
          >
            {{ i18n.t('success.title') }}
          </h2>
          <p class="mb-6" [style.color]="theme.textMutedColor">
            {{ i18n.t('success.message') }}
          </p>

          <!-- Slot details if available -->
          @if (selectedSlot()) {
            <div
              class="rounded-lg p-4 mb-6 text-left"
              [style.background-color]="theme.backgroundColor"
              [style.border]="'1px solid ' + theme.borderColor"
            >
              <h3 class="font-medium mb-2" [style.color]="theme.textColor">
                {{ i18n.t('success.requestedSlot') }}
              </h3>
              <p [style.color]="theme.textMutedColor">
                {{ formatDate(selectedSlot()!.date) }}<br />
                {{ i18n.formatTime(selectedSlot()!.startTime) }} - {{ i18n.formatTime(selectedSlot()!.endTime) }} {{ i18n.t('success.clock') }}
              </p>
            </div>
          }

          <!-- What happens next -->
          <div
            class="rounded-lg p-4 text-left mb-6"
            [style.background-color]="theme.backgroundColor"
            [style.border]="'1px solid ' + theme.borderColor"
          >
            <h3 class="font-medium mb-3" [style.color]="theme.textColor">{{ i18n.t('success.nextSteps') }}</h3>
            <ol class="space-y-2 text-sm" [style.color]="theme.textMutedColor">
              <li class="flex gap-2">
                <span
                  class="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium"
                  [style.background-color]="theme.primaryColor + '20'"
                  [style.color]="theme.primaryColor"
                >1</span>
                <span>{{ i18n.t('success.step1') }}</span>
              </li>
              <li class="flex gap-2">
                <span
                  class="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium"
                  [style.background-color]="theme.primaryColor + '20'"
                  [style.color]="theme.primaryColor"
                >2</span>
                <span>{{ i18n.t('success.step2') }}</span>
              </li>
              <li class="flex gap-2">
                <span
                  class="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium"
                  [style.background-color]="theme.primaryColor + '20'"
                  [style.color]="theme.primaryColor"
                >3</span>
                <span>{{ i18n.t('success.step3') }}</span>
              </li>
            </ol>
          </div>

          <!-- Request ID -->
          @if (successResponse()) {
            <p class="text-sm mb-4" [style.color]="theme.textMutedColor">
              {{ i18n.t('success.requestId') }}:
              <code
                class="px-2 py-1 rounded"
                [style.background-color]="theme.backgroundColor"
              >{{ successResponse()!.requestId.slice(0, 8) }}</code>
            </p>
          }

          <!-- New request button -->
          <button
            type="button"
            class="px-6 py-3 font-medium rounded-lg transition-colors duration-200"
            [style.background-color]="theme.primaryColor"
            [style.color]="theme.buttonTextColor"
            [style.border-radius]="theme.buttonRadius === 'full' ? '9999px' : theme.buttonRadius === 'xl' ? '0.75rem' : theme.buttonRadius === 'lg' ? '0.5rem' : theme.buttonRadius === 'md' ? '0.375rem' : theme.buttonRadius === 'sm' ? '0.25rem' : '0'"
            (click)="onDismissSuccess()"
          >
            {{ i18n.t('success.newRequest') }}
          </button>
        </div>
      </div>
    }
  `,
})
export class CalendarRendererComponent {
  /** Injected theme from fullwidth-preview */
  protected readonly themeSignal = inject(SECTION_THEME);
  protected readonly i18n = inject(PublicI18nService);

  // Signal inputs
  settingsInput = input<Record<string, unknown>>({});
  contentInput = input<Record<string, unknown>>({});
  slots = input<AggregatedSlot[]>([]);
  loadingInput = input<boolean>(false);
  previewModeInput = input<boolean>(true);
  submittingInput = input<boolean>(false);
  formErrorInput = input<string | null>(null);
  showSuccessInput = input<boolean>(false);
  successResponseInput = input<BookingRequestResponse | null>(null);
  timezoneInput = input<string>(DEFAULT_TIMEZONE);

  /** Computed theme for template access */
  protected get theme() {
    return this.themeSignal();
  }

  /** Settings accessor */
  protected readonly settings = computed(() => (this.settingsInput() as CalendarSettings) || {});

  /** Content accessor */
  protected readonly content = computed(() => (this.contentInput() as CalendarContent) || {});

  /** Preview mode accessor */
  protected readonly previewMode = computed(() => this.previewModeInput());

  /** Loading state accessor */
  protected readonly loading = computed(() => this.loadingInput());

  /** Submitting state accessor */
  protected readonly submitting = computed(() => this.submittingInput());

  /** Form error message accessor */
  protected readonly formErrorMessage = computed(() => this.formErrorInput());

  /** Show success accessor - internal signal for bidirectional control */
  private readonly _showSuccess = signal<boolean>(false);
  protected readonly showSuccess = computed(() => this._showSuccess());

  /** Success response accessor */
  protected readonly successResponse = computed(() => this.successResponseInput());

  /** Timezone accessor */
  protected readonly timezone = computed(() => this.timezoneInput() || DEFAULT_TIMEZONE);

  constructor() {
    // Sync showSuccess from input (side-effect: when showSuccessInput turns true, could trigger UI changes)
    effect(() => {
      this._showSuccess.set(this.showSuccessInput());
    });
  }

  /** Whether to show header (title or subtitle present) */
  protected readonly showHeader = computed(() => {
    const c = this.content();
    const s = this.settings();
    return (s.showTitle !== false && c.title) || (s.showSubtitle !== false && c.subtitle);
  });

  /** Background color from settings or theme */
  protected readonly backgroundColor = computed(() => {
    const s = this.settings();
    if (s.backgroundType === 'solid' && s.backgroundColor) {
      return s.backgroundColor;
    }
    if (s.backgroundType === 'transparent') {
      return 'transparent';
    }
    return this.theme.backgroundColor;
  });

  /** Container width CSS class based on theme */
  protected readonly containerWidthClass = computed(() => {
    const width = this.themeSignal().containerWidth || 'default';
    const map: Record<string, string> = {
      narrow: 'max-w-4xl',
      default: 'max-w-7xl',
      wide: 'max-w-[90rem]',
      full: 'max-w-full',
    };
    return map[width] || 'max-w-7xl';
  });

  /** Section spacing CSS classes based on theme */
  protected readonly sectionSpacingClass = computed(() => {
    const spacing = this.themeSignal().sectionSpacing || 'default';
    const map: Record<string, string> = {
      compact: 'py-8 sm:py-12',
      default: 'py-16 sm:py-24',
      relaxed: 'py-24 sm:py-32',
    };
    return map[spacing] || 'py-16 sm:py-24';
  });

  /** Mock slots for preview - generated once */
  private readonly mockSlots = signal<AggregatedSlot[]>(generateMockSlots());

  /** Display slots - use external slots in live mode, mock slots in preview mode */
  protected readonly displaySlots = computed(() => {
    if (this.previewMode()) {
      return this.mockSlots();
    }
    return this.slots();
  });

  /** Currently selected slot */
  protected readonly selectedSlot = signal<AggregatedSlot | null>(null);

  /** Emits when a slot is selected */
  slotSelected = output<AggregatedSlot>();

  /** Emits when the week changes (for fetching new slots) */
  weekChanged = output<WeekChangeEvent>();

  /** Emits when the booking form is submitted */
  submitRequest = output<BookingRequest>();

  /** Emits when the success modal is dismissed */
  successDismissed = output<void>();

  /** Handle slot selection */
  protected onSlotSelected(slot: AggregatedSlot): void {
    this.selectedSlot.set(slot);
    this.slotSelected.emit(slot);
  }

  /** Handle week change */
  protected onWeekChanged(range: { from: string; to: string }): void {
    this.weekChanged.emit(range);
  }

  /** Handle form submission */
  protected onSubmitRequest(request: BookingRequest): void {
    this.submitRequest.emit(request);
  }

  /** Clear slot selection to go back to calendar view */
  protected clearSlotSelection(): void {
    this.selectedSlot.set(null);
  }

  /** Reset to initial state (after successful booking) */
  public resetToStart(): void {
    this.selectedSlot.set(null);
  }

  /** Handle dismissing the success modal */
  protected onDismissSuccess(): void {
    this.selectedSlot.set(null);
    this.successDismissed.emit();
  }

  /** Format date for display */
  protected formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString(this.i18n.getDateLocale(), {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }
}
